import React, { useState, useEffect } from 'react';
import {
  Box, Heading, Spinner, Center, Alert, AlertIcon, Button, HStack,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, FormControl, FormLabel, Input, InputGroup, InputRightElement,
  IconButton, Select, useDisclosure, useToast
} from '@chakra-ui/react';
import { useRecoilValue } from 'recoil';
import { AddIcon, ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import UserManagement from '../components/admin/UserManagement';
import { adminService } from '../services/api';
import { authState } from '../state/authState';
import { validatePassword } from '../utils/passwordValidation';

const defaultNewUser = {
  email: '',
  username: '',
  password: '',
  firstName: '',
  lastName: '',
  role: 'user'
};

export const AdminUsersPage = () => {
  const auth = useRecoilValue(authState);
  const currentUserRole = auth.user?.role;
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newUser, setNewUser] = useState(defaultNewUser);
  const [isCreating, setIsCreating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editShowPassword, setEditShowPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await adminService.getUsers();
        setUsers(response.data.users);
      } catch (err) {
        setError('Failed to load users');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleUpdate = (userId, updatedUser) => {
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, ...updatedUser } : u));
  };

  const handleNewUserChange = (field, value) => {
    setNewUser(prev => ({ ...prev, [field]: value }));
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.username || !newUser.password || !newUser.firstName || !newUser.lastName) {
      toast({ title: 'All fields are required', status: 'warning', duration: 2000 });
      return;
    }

    const passwordError = validatePassword(newUser.password);
    if (passwordError) {
      toast({ title: passwordError, status: 'warning', duration: 2000 });
      return;
    }

    setIsCreating(true);
    try {
      const response = await adminService.createUser(newUser);
      setUsers(prev => [response.data.user, ...prev]);
      toast({ title: 'User created', status: 'success', duration: 2000 });
      setNewUser(defaultNewUser);
      onClose();
    } catch (err) {
      toast({
        title: 'Failed to create user',
        description: err.response?.data?.error,
        status: 'error',
        duration: 3000
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleModalClose = () => {
    setNewUser(defaultNewUser);
    setShowPassword(false);
    onClose();
  };

  const handleEditClick = (user) => {
    setEditUser({
      id: user.id,
      email: user.email,
      username: user.username,
      firstName: user.first_name,
      lastName: user.last_name,
      role: user.role,
      password: ''
    });
    onEditOpen();
  };

  const handleEditChange = (field, value) => {
    setEditUser(prev => ({ ...prev, [field]: value }));
  };

  const handleEditModalClose = () => {
    setEditUser(null);
    setEditShowPassword(false);
    onEditClose();
  };

  const handleSaveEdit = async () => {
    if (!editUser.email || !editUser.username || !editUser.firstName || !editUser.lastName) {
      toast({ title: 'Email, username, and name are required', status: 'warning', duration: 2000 });
      return;
    }

    if (editUser.password) {
      const passwordError = validatePassword(editUser.password);
      if (passwordError) {
        toast({ title: passwordError, status: 'warning', duration: 2000 });
        return;
      }
    }

    setIsSaving(true);
    try {
      const payload = {
        email: editUser.email,
        username: editUser.username,
        firstName: editUser.firstName,
        lastName: editUser.lastName,
        ...(currentUserRole === 'super_admin' && { role: editUser.role }),
        ...(editUser.password && { password: editUser.password })
      };
      const response = await adminService.updateUser(editUser.id, payload);
      handleUpdate(editUser.id, response.data.user);
      toast({ title: 'User updated', status: 'success', duration: 2000 });
      handleEditModalClose();
    } catch (err) {
      toast({
        title: 'Failed to update user',
        description: err.response?.data?.error,
        status: 'error',
        duration: 3000
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Center py={20}>
        <Spinner size="xl" color="red.500" />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <HStack justify="space-between" mt="15px" mb={6}>
        <Heading size="lg">User Management</Heading>
        <Button leftIcon={<AddIcon />} colorScheme="green" size="sm" onClick={onOpen}>
          Add User
        </Button>
      </HStack>
      <Box bg="white" borderRadius="lg" boxShadow="sm" p={4}>
        <UserManagement users={users} onUpdate={handleUpdate} onEdit={handleEditClick} currentUserRole={currentUserRole} />
      </Box>

      <Modal isOpen={isOpen} onClose={handleModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New User</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={3} isRequired>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => handleNewUserChange('email', e.target.value)}
                placeholder="user@example.com"
              />
            </FormControl>
            <FormControl mb={3} isRequired>
              <FormLabel>Username</FormLabel>
              <Input
                value={newUser.username}
                onChange={(e) => handleNewUserChange('username', e.target.value)}
                placeholder="username"
              />
            </FormControl>
            <HStack mb={3} spacing={3}>
              <FormControl isRequired>
                <FormLabel>First Name</FormLabel>
                <Input
                  value={newUser.firstName}
                  onChange={(e) => handleNewUserChange('firstName', e.target.value)}
                  placeholder="John"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Last Name</FormLabel>
                <Input
                  value={newUser.lastName}
                  onChange={(e) => handleNewUserChange('lastName', e.target.value)}
                  placeholder="Doe"
                />
              </FormControl>
            </HStack>
            <FormControl mb={3} isRequired>
              <FormLabel>Password</FormLabel>
              <InputGroup>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={newUser.password}
                  onChange={(e) => handleNewUserChange('password', e.target.value)}
                  placeholder="Min. 8 characters"
                />
                <InputRightElement>
                  <IconButton
                    icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                    onClick={() => setShowPassword(prev => !prev)}
                    variant="ghost"
                    size="sm"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>
            {currentUserRole === 'super_admin' && (
              <FormControl>
                <FormLabel>Role</FormLabel>
                <Select
                  value={newUser.role}
                  onChange={(e) => handleNewUserChange('role', e.target.value)}
                >
                  <option value="user">User</option>
                  <option value="manager">Manager</option>
                  <option value="super_admin">Admin</option>
                </Select>
              </FormControl>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleModalClose}>Cancel</Button>
            <Button colorScheme="green" onClick={handleCreateUser} isLoading={isCreating}>
              Create User
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {editUser && (
        <Modal isOpen={isEditOpen} onClose={handleEditModalClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Edit User</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <FormControl mb={3} isRequired>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={editUser.email}
                  onChange={(e) => handleEditChange('email', e.target.value)}
                />
              </FormControl>
              <FormControl mb={3} isRequired>
                <FormLabel>Username</FormLabel>
                <Input
                  value={editUser.username}
                  onChange={(e) => handleEditChange('username', e.target.value)}
                />
              </FormControl>
              <HStack mb={3} spacing={3}>
                <FormControl isRequired>
                  <FormLabel>First Name</FormLabel>
                  <Input
                    value={editUser.firstName}
                    onChange={(e) => handleEditChange('firstName', e.target.value)}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Last Name</FormLabel>
                  <Input
                    value={editUser.lastName}
                    onChange={(e) => handleEditChange('lastName', e.target.value)}
                  />
                </FormControl>
              </HStack>
              <FormControl mb={3}>
                <FormLabel>New Password</FormLabel>
                <InputGroup>
                  <Input
                    type={editShowPassword ? 'text' : 'password'}
                    value={editUser.password}
                    onChange={(e) => handleEditChange('password', e.target.value)}
                    placeholder="Leave blank to keep current"
                  />
                  <InputRightElement>
                    <IconButton
                      icon={editShowPassword ? <ViewOffIcon /> : <ViewIcon />}
                      onClick={() => setEditShowPassword(prev => !prev)}
                      variant="ghost"
                      size="sm"
                      aria-label={editShowPassword ? 'Hide password' : 'Show password'}
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>
              {currentUserRole === 'super_admin' && (
                <FormControl>
                  <FormLabel>Role</FormLabel>
                  <Select
                    value={editUser.role}
                    onChange={(e) => handleEditChange('role', e.target.value)}
                  >
                    <option value="user">User</option>
                    <option value="manager">Manager</option>
                    <option value="super_admin">Admin</option>
                  </Select>
                </FormControl>
              )}
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={handleEditModalClose}>Cancel</Button>
              <Button colorScheme="blue" onClick={handleSaveEdit} isLoading={isSaving}>
                Save Changes
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
};

export default AdminUsersPage;
